/**
 * ============================================
 * Display.tsx — Основной компонент TV-экрана
 * ============================================
 * Публичный экран для показа расписания автобусов.
 * Оптимизирован для Digital Signage / больших экранов.
 *
 * Структура:
 * - Шапка: название, дата, часы
 * - Таблица: список рейсов
 * - Подвал: бегущая строка, индикатор подключения
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSchedule } from './useSchedule';
import { ScheduleRow } from './ScheduleRow';
import { ConnectionBadge } from './ConnectionBadge';
import type { BusScheduleItem } from './useSchedule';

// Подключаем кастомные стили
import './display.css';

// ============================================
// Хелперы форматирования
// ============================================

/** Месяцы на русском */
const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** Дни недели на русском */
const WEEKDAYS = [
  'Воскресенье', 'Понедельник', 'Вторник', 'Среда',
  'Четверг', 'Пятница', 'Суббота',
];

/**
 * Форматирует текущую дату в строку: "Понедельник, 15 апреля 2026"
 */
function formatDate(date: Date): string {
  const weekday = WEEKDAYS[date.getDay()];
  const day = date.getDate();
  const month = MONTHS[date.getMonth()];
  const year = date.getFullYear();
  return `${weekday}, ${day} ${month} ${year}`;
}

/**
 * Форматирует время в строку HH:MM:SS
 */
function formatTime(date: Date): { time: string; seconds: string } {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return { time: `${h}:${m}`, seconds: `:${s}` };
}

function timeToMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
}

const DISPLAY_WINDOW_HOURS = 20;
const KEEP_RECENT_MINUTES = 45;

// ============================================
// Компонент: Часы
// ============================================

/**
 * Компонент часов с обновлением каждую секунду.
 * Показывает время крупным шрифтом для видимости с расстояния.
 */
const LiveClock: React.FC = React.memo(function LiveClock() {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const { time, seconds } = formatTime(now);

  return (
    <div className="tv-header__time" aria-label={`Текущее время ${time}${seconds}`}>
      <span>{time}</span>
      <span className="tv-header__time-seconds">{seconds}</span>
    </div>
  );
});

// ============================================
// Компонент: Шапка
// ============================================

/**
 * Шапка экрана с названием, датой и часами.
 * Всегда видна в верхней части экрана.
 */
const DisplayHeader: React.FC = React.memo(function DisplayHeader() {
  const [dateStr, setDateStr] = useState(() => formatDate(new Date()));

  // Обновляем дату при монтировании и затем каждый час
  useEffect(() => {
    setDateStr(formatDate(new Date()));
    const timer = setInterval(() => {
      setDateStr(formatDate(new Date()));
    }, 60_000);
    return () => clearInterval(timer);
  }, []);

  return (
    <header className="tv-header" role="banner">
      {/* Левая часть — название */}
      <div>
        <h1 className="tv-header__title">
          <span className="tv-header__title-accent">РАСПИСАНИЕ</span>
          <br />
          АВТОБУСОВ
        </h1>
      </div>

      {/* Центр — дата */}
      <div className="tv-header__date" aria-label={`Сегодня ${dateStr}`}>
        {dateStr}
      </div>

      {/* Правая часть — часы */}
      <LiveClock />
    </header>
  );
});

// ============================================
// Компонент: Таблица расписания
// ============================================

interface ScheduleTableProps {
  /** Список рейсов */
  items: BusScheduleItem[];
  /** Множество ID обновлённых строк */
  updatedIds: Set<string>;
  /** Множество ID новых строк */
  newIds: Set<string>;
  /** ID активного слота */
  activeId: string | null;
}

/**
 * Таблица расписания с фиксированными колонками.
 * Использует CSS Grid для раскладки.
 */
const ScheduleTable: React.FC<ScheduleTableProps> = React.memo(
  function ScheduleTable({ items, updatedIds, newIds, activeId }) {
    const splitIndex = Math.ceil(items.length / 2);
    const columns = [items.slice(0, splitIndex), items.slice(splitIndex)].filter((column) => column.length > 0);

    if (items.length === 0) {
      return (
        <div className="tv-empty">
          <div className="tv-empty__icon">🚌</div>
          <div className="tv-empty__text">Нет рейсов на сегодня</div>
          <div className="tv-empty__subtext">Расписание будет обновлено автоматически</div>
        </div>
      );
    }

    return (
      <div className="tv-schedule" role="region" aria-label="Расписание автобусов">
        <div className={`tv-schedule__boards tv-schedule__boards--${columns.length}`} role="presentation">
          {columns.map((columnItems, columnIndex) => (
            <div key={`column-${columnIndex}`} className="tv-schedule__board">
              <div className="tv-schedule__grid" role="table">
                <div className="tv-schedule__col-header" role="columnheader">
                  ВРЕМЯ
                </div>
                <div className="tv-schedule__col-header" role="columnheader">
                  МАРШРУТ
                </div>
                <div className="tv-schedule__col-header" role="columnheader">
                  НАПРАВЛЕНИЕ
                </div>
                <div className="tv-schedule__col-header" role="columnheader">
                  ПЛАТФ.
                </div>
                <div className="tv-schedule__col-header" role="columnheader">
                  СТАТУС
                </div>

                {columnItems.map((item) => (
                  <ScheduleRow
                    key={item.id}
                    item={item}
                    isUpdated={updatedIds.has(item.id)}
                    isNew={newIds.has(item.id)}
                    isActive={item.id === activeId}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

// ============================================
// Компонент: Бегущая строка
// ============================================

/**
 * Бегущая строка с информационными сообщениями.
 * Дублирует текст для бесшовной анимации.
 */
const Ticker: React.FC<{ itemsCount: number }> = React.memo(function Ticker({ itemsCount }) {
  const messages = [
    'Добро пожаловать на автовокзал!',
    'Следите за обновлениями расписания на экране',
    'Информация по телефону: 8 (800) 555-35-35',
    'Просьба не оставлять багаж без присмотра',
    'Платформа может быть изменена — следите за объявлениями',
    `На табло показано рейсов: ${itemsCount}`,
  ];

  // Собираем текст с разделителями
  const tickerText = messages.join('  ●  ');

  return (
    <div className="tv-ticker" aria-label="Информационные объявления">
      <div className="tv-ticker__track">
        {/* Дублируем текст для бесшовной анимации */}
        <span className="tv-ticker__text">{tickerText}</span>
        <span className="tv-ticker__text">{tickerText}</span>
      </div>
    </div>
  );
});

// ============================================
// Компонент: Подвал
// ============================================

interface DisplayFooterProps {
  /** Состояние подключения */
  connection: import('./useSchedule').ConnectionState;
  /** Число видимых рейсов */
  itemsCount: number;
}

/**
 * Подвал с бегущей строкой и индикатором подключения.
 */
const DisplayFooter: React.FC<DisplayFooterProps> = React.memo(
  function DisplayFooter({ connection, itemsCount }) {
    return (
      <footer className="tv-footer" role="contentinfo">
        <Ticker itemsCount={itemsCount} />
        <ConnectionBadge state={connection} />
      </footer>
    );
  }
);

// ============================================
// Компонент: Индикатор обновления
// ============================================

/**
 * Маленький индикатор "Обновление..." в углу экрана.
 */
const UpdateIndicator: React.FC = React.memo(function UpdateIndicator() {
  return (
    <div className="tv-update-indicator" role="status" aria-live="polite">
      <div className="tv-loading__spinner" />
      <span>Обновление...</span>
    </div>
  );
});

// ============================================
// Компонент: Оффлайн-предупреждение
// ============================================

/**
 * Жёлтая плашка при отсутствии связи с сервером.
 */
const OfflineBanner: React.FC = React.memo(function OfflineBanner() {
  return (
    <div className="tv-offline-banner" role="alert" aria-live="assertive">
      ⚠ Нет связи с сервером — показаны последние полученные данные
    </div>
  );
});

// ============================================
// Главный компонент: Display
// ============================================

/**
 * Основной компонент TV-экрана расписания автобусов.
 *
 * Особенности:
 * - Оптимизирован для чтения с расстояния 3-5 метров
 * - Использует vmin для адаптивности под любое разрешение
 * - GPU-ускоренные анимации
 * - Автообновление данных каждые 30 секунд
 * - SSE real-time обновления
 * - Оффлайн-режим с кэшированием
 */
const Display: React.FC = () => {
  const { data, loading, connection, error, isUpdating } = useSchedule();
  const [now, setNow] = useState(() => new Date());

  // Отслеживаем обновлённые и новые строки для анимаций
  const [prevIds, setPrevIds] = useState<Set<string>>(new Set());
  const [updatedIds, setUpdatedIds] = useState<Set<string>>(new Set());
  const [newIds, setNewIds] = useState<Set<string>>(new Set());

  // Сортируем данные по времени
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => a.time.localeCompare(b.time));
  }, [data]);

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 30_000);
    return () => clearInterval(timer);
  }, []);

  const visibleData = useMemo(() => {
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const maxMinutes = nowMinutes + DISPLAY_WINDOW_HOURS * 60;

    const upcoming = sortedData.filter((item) => {
      const itemMinutes = timeToMinutes(item.time);
      return itemMinutes >= nowMinutes - KEEP_RECENT_MINUTES && itemMinutes <= maxMinutes;
    });

    if (upcoming.length > 0) {
      return upcoming;
    }

    return sortedData;
  }, [now, sortedData]);

  const activeId = useMemo(() => {
    if (visibleData.length === 0) {
      return null;
    }

    const activeItem =
      visibleData.find((item) => item.status === 'boarding') ??
      visibleData.find((item) => item.status === 'scheduled' || item.status === 'delayed') ??
      visibleData[0];

    return activeItem?.id ?? null;
  }, [visibleData]);

  // Определяем обновлённые и новые строки при изменении данных
  useEffect(() => {
    const currentIds = new Set(sortedData.map((item) => item.id));
    const newItems = new Set<string>();
    const updated = new Set<string>();

    for (const item of sortedData) {
      if (!prevIds.has(item.id)) {
        newItems.add(item.id);
      } else {
        // Простая проверка — если updatedAt есть и свежее последнего рендера
        if (item.updatedAt) {
          updated.add(item.id);
        }
      }
    }

    setNewIds(newItems);
    setUpdatedIds(updated);
    setPrevIds(currentIds);

    // Очищаем флаги анимации через 1 секунду
    const timer = setTimeout(() => {
      setNewIds(new Set());
      setUpdatedIds(new Set());
    }, 1000);

    return () => clearTimeout(timer);
  }, [prevIds, sortedData]);

  // Показываем оффлайн-баннер если нет связи и есть данные
  const showOfflineBanner = connection === 'offline' && sortedData.length > 0;

  // Показываем индикатор обновления
  const showUpdateIndicator = isUpdating && !loading;

  return (
    <div className="tv-display">
      {/* Оффлайн-предупреждение */}
      {showOfflineBanner && <OfflineBanner />}

      {/* Индикатор обновления */}
      {showUpdateIndicator && <UpdateIndicator />}

      {/* Шапка */}
      <DisplayHeader />

      {/* Основное содержимое */}
      <main className="tv-main">
        {loading && visibleData.length === 0 ? (
          // Первоначальная загрузка
          <div className="tv-loading">
            <div className="tv-loading__spinner" />
            <span>Загрузка расписания...</span>
          </div>
        ) : error && visibleData.length === 0 ? (
          // Ошибка загрузки и нет кэша
          <div className="tv-empty">
            <div className="tv-empty__icon">⚠</div>
            <div className="tv-empty__text">Не удалось загрузить расписание</div>
            <div className="tv-empty__subtext">{error}</div>
          </div>
        ) : (
          // Таблица расписания
          <ScheduleTable items={visibleData} updatedIds={updatedIds} newIds={newIds} activeId={activeId} />
        )}
      </main>

      {/* Подвал */}
      <DisplayFooter connection={connection} itemsCount={visibleData.length} />
    </div>
  );
};

export default Display;
